<template>
  <div class="permanent-candidate-root">
    <QHeader class="bg-white text-primary">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" @click="onGoBack" />
        <QToolbarTitle>
          {{ recruitment.isUnlocked() ? $t("ETAB.PAGES.EXTRA.@TITLE_PLACEHOLDER") : $t("ETAB.PAGES.EXTRA.@TITLE") }}
        </QToolbarTitle>
        <QBtn
          v-if="recruitment.isUnlocked() && !extra.isIgnored() && extra.isSelected()"
          class="q-pa-xs"
          flat
          color="negative"
          no-caps
          dense
          icon-right="report_problem"
          :label="$t('ETAB.PAGES.RECRUITMENT.@REPORT_CANDIDATE')"
          @click="openReportModal"
        />
      </QToolbar>
    </QHeader>

    <QPageContainer>
      <QPage>
        <router-view v-slot="{ Component }">
          <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
            <component :is="Component" />
          </transition>
        </router-view>
      </QPage>
    </QPageContainer>

    <QFooter class="bg-transparent">
      <FooterBtnsWrapper>
        <ExtraBtnIcon
          v-if="canContact"
          class="full-width"
          color="secondary"
          type="button"
          no-caps
          :all-space="true"
          icon-name="icon-extra-magic"
          :btn-label="$t('ETAB.PAGES.EXTRA.@CONTACT_EXTRA')"
          @click="onContactExtra"
        />
        <ExtraBtnIcon
          v-if="canActivate"
          class="full-width"
          color="primary"
          type="button"
          no-caps
          :all-space="true"
          icon-name="icon-extra-magic"
          :btn-label="$t('ETAB.PAGES.EXTRA.@ACTIVATE_ACCESS_TO_CONTACTS')"
          @click="activateDemand"
        />
        <QBanner v-if="extra.hasRefused()" class="full-width bg-grey text-center text-bold">
          <span class="text-h3">{{ $t("ETAB.PAGES.EXTRA.@REFUSED_EXTRA") }}</span>
        </QBanner>
      </FooterBtnsWrapper>
      <Contact
        v-if="canAccessContactInformations"
        :has-cancelled="extra.hasRefused()"
        :phone="extra.getIdentity().phone"
        :email="extra.getIdentity().email"
      />
    </QFooter>
    <ReportCandidateModal v-model:open="reportModalOpened" @refresh="goBacktoRecruitmentDetail" />
  </div>
</template>

<script setup>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { ScrollManager } from "@core/managers/scroll.manager";
  import { ExtraService, RecruitmentCandidatesService } from "@core/services";
  import { ETAB_ROUTES } from "@etab/config";
  import {
    GETTER_RECRUITMENT_GET_CURRENT,
    GETTER_EXTRA_GET_CURRENT,
    GETTER_REPORT_MODAL_GET,
    ACTION_EXTRA_GET_BY_ID,
    ACTION_REPORT_MODAL_SET,
    ACTION_REPORT_CANDIDATE_SET,
    ACTION_REPORT_DEMAND_SET,
    ACTION_RECRUITMENT_GET_BY_ID,
  } from "@etab/stores/types";
  import { useExtraStore } from "@etab/stores/extra";
  import { useRecruitmentStore } from "@etab/stores/recruitment";
  import { useReportCandidateStore } from "@etab/stores/report-candidate";
  import { computed, onMounted } from "vue";
  import { useTransition } from "@core/composables";
  import { useRoute, useRouter } from "vue-router";
  import { LoadingManager } from "@core/managers/loading.manager";

  import Contact from "@etab/components/common/Contact.component.vue";
  import ReportCandidateModal from "@etab/components/recruitments/detail/ReportCandidate.modal.vue";

  const { transition } = useTransition();
  const recruitmentStore = useRecruitmentStore();
  const reportCandidateStore = useReportCandidateStore();
  const extraStore = useExtraStore();

  const route = useRoute();
  const router = useRouter();

  const demandId = computed(() => {
    return route.params.demandId;
  });

  const demandType = computed(() => {
    return route.params.demandType;
  });

  const recruitment = computed(() => {
    return recruitmentStore[GETTER_RECRUITMENT_GET_CURRENT];
  });

  const extra = computed(() => {
    return extraStore[GETTER_EXTRA_GET_CURRENT];
  });

  const canAccessContactInformations = computed(() => {
    return ExtraService.canAccessContactInformations({ extra: extra.value, recruitment: recruitment.value });
  });

  const canContact = computed(() => {
    return (
      recruitment.value.isUnlocked() &&
      !recruitment.value.isExpired() &&
      !recruitment.value.isArchived() &&
      !extra.value.hasRefused() &&
      !extra.value.isSelected()
    );
  });

  const reportModalOpened = computed(() => {
    return reportCandidateStore[GETTER_REPORT_MODAL_GET];
  });

  const canActivate = computed(() => {
    return !recruitment.value.isUnlocked() && !recruitment.value.isExpired() && !recruitment.value.isArchived();
  });

  // Methods
  const openReportModal = function () {
    reportCandidateStore[ACTION_REPORT_MODAL_SET](true);
    reportCandidateStore[ACTION_REPORT_CANDIDATE_SET]({ candidateId: extra.value.getId(), candidateFirstname: extra.value.getFirstname() });
    reportCandidateStore[ACTION_REPORT_DEMAND_SET](demandId.value);
  };

  const refresh = async function ({ displayLoading = true } = {}) {
    try {
      if (displayLoading) {
        LoadingManager.show();
      }
      const type = recruitment.value.getType();
      const extraId = extra.value.getId();
      await Promise.all([
        recruitmentStore[ACTION_RECRUITMENT_GET_BY_ID](demandId.value),
        extraStore[ACTION_EXTRA_GET_BY_ID]({ extraId, demandId: demandId.value, type }),
      ]);
    } catch (error) {
      const message = (error && error.message) || "ERRORS.@SOMETHING_WENT_WRONG";
      NotificationsManager.error({ message });
      throw error;
    } finally {
      LoadingManager.hide();
    }
  };

  const onGoBack = function () {
    const demandTypeValue = demandType.value;
    const demandIdValue = demandId.value;
    const query = route.query;

    if (extra.value.isSelected() || extra.value.hasRefused()) {
      return router
        .push({
          name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.contacted.name,
          params: { demandId: demandIdValue, demandType: demandTypeValue },
          query: { ...query },
        })
        .catch((suppressedError) => {});
    }
    if (extra.value.isIgnored()) {
      return router
        .push({
          name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.ignored.name,
          params: { demandId: demandIdValue, demandType: demandTypeValue },
          query: { ...query },
        })
        .catch((suppressedError) => {});
    }
    return router
      .push({
        name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.applied.name,
        params: { demandId: demandIdValue, demandType: demandTypeValue },
        query: { ...query },
      })
      .catch((suppressedError) => {});
  };

  const goBacktoRecruitmentDetail = function () {
    const demandTypeValue = demandType.value;
    const demandIdValue = demandId.value;
    const query = route.query;
    router
      .replace({
        name: ETAB_ROUTES.dashboard.recruitment.permanent.detail.contacted.name,
        params: { demandId: demandIdValue, demandType: demandTypeValue },
        query: { ...query },
      })
      .catch((suppressedError) => {});
  };

  const onContactExtra = async function () {
    if (!extra.value.isSelected()) {
      try {
        LoadingManager.show();
        const demandId = recruitment.value.getDemandId();
        const candidateId = extra.value.getId();
        await RecruitmentCandidatesService.contactPermanentCandidate({ demandId, candidateId });
        const options = { timeout: 5000 }; // longer time because the message is long
        NotificationsManager.success({ message: "SUCCESS.@CANDIDATE_CONTACTED_SUCCESS", options });
      } catch (error) {
        const message = (error && error.message) || "ERRORS.@SOMETHING_WENT_WRONG";
        NotificationsManager.error({ message });
        throw error;
      } finally {
        refresh();
      }
    }
  };

  const activateDemand = function () {
    const demandTypeValue = demandType.value;
    const demandIdValue = demandId.value;
    // go to activation page
    router
      .push({
        name: ETAB_ROUTES.dashboard.recruitment.permanent.activation.payment.name,
        // save the demandId in order to pay for this demand
        params: { demandId: demandIdValue, demandType: demandTypeValue },
        // query the extraId in order to go back to the extra profile once it's paid
        query: { extraId: extra.value.getId() },
      })
      .catch((suppressedError) => {});
  };

  // Created
  ScrollManager.scrollToTop();

  // Mounted
  onMounted(() => {
    // stop loading started in route
    LoadingManager.hide();
  });
</script>

<style lang="scss">
  .permanent-candidate-root {
    background-color: $body-background-light-grey;

    .q-page {
      overflow: hidden; // remove the scroll during transition
      padding: 0 15px 15px;
    }
  }
</style>
